// MBA Lab — Leadership Practice Journal UI. Imports only lib/core.js and
// lib/journal-model.js. Three tabs, one store, matching the plan's "one
// local-first store and one privacy/export/delete story."
import { store, download, trackToolAction } from './lib/core.js';
import {
  TOTAL_DAYS,
  createInitialState,
  getPromptForDay,
  getPhaseForDay,
  getSuggestedLogTypeForDay,
  getCurrentDay,
  isDailyPracticeComplete,
  logDailyEntry,
  setClosingPlan,
  addWeeklyReflection,
  addDecisionEntry,
  recordDecisionOutcome,
  parseSavedState,
  serializeForSave,
  buildMarkdownExport,
} from './lib/journal-model.js';
import { LOG_TYPE_LABELS } from './lib/journal-prompts.js';

const TOOL_NAME = 'leadership-practice-journal';
const AUTOSAVE_DEBOUNCE_MS = 400;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

function formatDate(ts) {
  if (typeof ts !== 'number') return '';
  return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** " · Self" or " · Self · Ego-pattern log" - appended to the day
 * label. Empty string if the day somehow falls outside every phase, so the
 * label degrades to just "Day N of 90" rather than showing "undefined". */
function formatDayMeta(day) {
  const phase = getPhaseForDay(day);
  if (!phase) return '';
  const logType = getSuggestedLogTypeForDay(day);
  const logLabel = logType ? LOG_TYPE_LABELS[logType] : null;
  return logLabel ? ` · ${phase.name} · ${logLabel}` : ` · ${phase.name}`;
}

class LeadershipPracticeJournal {
  constructor() {
    this.cacheElements();
    this.journalStore = store(TOOL_NAME);
    this.saveTimer = null;
    this.hasEngaged = false;
    this.startOverConfirming = false;
    this.startOverTimer = null;

    this.state = this.loadInitialState();
    this.wireEvents();
    this.renderAll();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.els = {
      tabs: Array.from(document.querySelectorAll('[data-journal-tab]')),
      panels: Array.from(document.querySelectorAll('[data-journal-panel]')),

      dailyPrompt: byId('journal-daily-prompt'),
      dailyDayLabel: byId('journal-daily-day-label'),
      dailyForm: byId('journal-daily-form'),
      dailyReflectionInput: byId('journal-daily-reflection'),
      dailyHistory: byId('journal-daily-history'),
      dailyCompleteBanner: byId('journal-daily-complete-banner'),
      dailyActiveForm: byId('journal-daily-active-form'),
      closingForm: byId('journal-closing-form'),
      closingContinuingInput: byId('journal-closing-continuing'),
      closingFocusInput: byId('journal-closing-focus'),
      closingSaved: byId('journal-closing-saved'),
      closingSavedContinuing: byId('journal-closing-saved-continuing'),
      closingSavedFocus: byId('journal-closing-saved-focus'),

      weeklyForm: byId('journal-weekly-form'),
      weeklyPatternInput: byId('journal-weekly-pattern'),
      weeklyChangeInput: byId('journal-weekly-change'),
      weeklyFocusInput: byId('journal-weekly-focus'),
      weeklyHistory: byId('journal-weekly-history'),

      decisionForm: byId('journal-decision-form'),
      decisionInput: byId('journal-decision-decision'),
      factsInput: byId('journal-decision-facts'),
      assumptionsInput: byId('journal-decision-assumptions'),
      emotionalStateInput: byId('journal-decision-emotional-state'),
      stakeholdersInput: byId('journal-decision-stakeholders'),
      expectedInput: byId('journal-decision-expected'),
      decisionHistory: byId('journal-decision-history'),

      exportBtn: byId('journal-export'),
      startOverBtn: byId('journal-start-over'),
      actionFeedback: byId('journal-action-feedback'),
    };
  }

  loadInitialState() {
    const saved = this.journalStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  wireEvents() {
    for (const tab of this.els.tabs) {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.journalTab));
      tab.addEventListener('keydown', (e) => this.handleTabKeydown(e));
    }

    this.els.dailyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleLogDailyEntry();
    });
    this.els.closingForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSaveClosingPlan();
    });
    this.els.weeklyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAddWeeklyReflection();
    });
    this.els.decisionForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAddDecisionEntry();
    });

    this.els.exportBtn.addEventListener('click', () => this.handleExport());
    this.els.startOverBtn.addEventListener('click', () => this.handleStartOver());
  }

  /** WAI-ARIA APG tablist pattern (audit finding: role="tab"/"tablist" was
   * correct but only click was wired, so arrow keys did nothing). Native
   * Tab-key focus + Enter/Space already worked via the plain click handler
   * above and stays untouched here - this only adds Left/Right/Home/End,
   * with automatic activation (moving focus also switches the panel),
   * matching this tool's existing click-activates-immediately behavior. */
  handleTabKeydown(e) {
    const key = e.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;
    e.preventDefault();

    const tabs = this.els.tabs;
    const currentIndex = tabs.indexOf(e.target);
    if (currentIndex === -1) return;

    let nextIndex;
    if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = tabs.length - 1;
    else if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else nextIndex = (currentIndex + 1) % tabs.length;

    const nextTab = tabs[nextIndex];
    nextTab.focus();
    this.selectTab(nextTab.dataset.journalTab);
  }

  selectTab(tabName) {
    for (const tab of this.els.tabs) {
      const active = tab.dataset.journalTab === tabName;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.classList.toggle('is-active', active);
    }
    for (const panel of this.els.panels) {
      panel.hidden = panel.dataset.journalPanel !== tabName;
    }
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  renderAll() {
    this.renderDailyTab();
    this.renderWeeklyHistory();
    this.renderDecisionHistory();
  }

  // --- 90-day practice tab ---------------------------------------------

  renderDailyTab() {
    const day = getCurrentDay(this.state);
    const complete = isDailyPracticeComplete(this.state);

    this.els.dailyActiveForm.hidden = complete;
    this.els.dailyCompleteBanner.hidden = !complete;

    if (!complete) {
      this.els.dailyDayLabel.textContent = `Day ${day} of ${TOTAL_DAYS}${formatDayMeta(day)}`;
      this.els.dailyPrompt.textContent = getPromptForDay(day);
      this.els.dailyReflectionInput.value = '';
    }

    if (complete && this.state.closingPlan) {
      this.els.closingForm.hidden = true;
      this.els.closingSaved.hidden = false;
      this.els.closingSavedContinuing.textContent = this.state.closingPlan.mostWorthContinuing;
      this.els.closingSavedFocus.textContent = this.state.closingPlan.nextFocus;
    } else {
      this.els.closingForm.hidden = !complete;
      this.els.closingSaved.hidden = true;
    }

    this.renderDailyHistory();
  }

  renderDailyHistory() {
    clearChildren(this.els.dailyHistory);
    const days = Object.keys(this.state.dailyPractice.entries)
      .map(Number)
      .sort((a, b) => b - a); // newest first

    for (const day of days) {
      const entry = this.state.dailyPractice.entries[String(day)];
      const item = document.createElement('div');
      item.className = 'tool-journal-history-item';

      // h3, not h4 - the containing "Past days" section header is an h2
      // (content-section-title), so this is one level down, not two.
      const title = document.createElement('h3');
      title.textContent = `Day ${day}${formatDayMeta(day)}: ${getPromptForDay(day)}`;
      item.appendChild(title);

      const body = document.createElement('p');
      body.textContent = entry.reflection;
      item.appendChild(body);

      if (entry.completedAt) {
        const meta = document.createElement('span');
        meta.className = 'tool-journal-history-meta';
        meta.textContent = formatDate(entry.completedAt);
        item.appendChild(meta);
      }

      this.els.dailyHistory.appendChild(item);
    }
  }

  handleLogDailyEntry() {
    const day = getCurrentDay(this.state);
    const reflection = this.els.dailyReflectionInput.value.trim();
    if (!reflection) return;

    this.state = logDailyEntry(this.state, day, reflection, Date.now());
    this.markEngaged();
    this.renderDailyTab();
    this.scheduleAutosave();
    this.showActionFeedback(`Day ${day} saved.`);
    trackToolAction(TOOL_NAME, 'result', { tab: 'daily-practice', day });
  }

  handleSaveClosingPlan() {
    const mostWorthContinuing = this.els.closingContinuingInput.value.trim();
    const nextFocus = this.els.closingFocusInput.value.trim();
    if (!mostWorthContinuing && !nextFocus) return;

    this.state = setClosingPlan(this.state, { mostWorthContinuing, nextFocus }, Date.now());
    this.renderDailyTab();
    this.scheduleAutosave();
    this.showActionFeedback('Closing plan saved.');
    trackToolAction(TOOL_NAME, 'result', { tab: 'closing-plan' });
  }

  // --- Weekly reflection tab ---------------------------------------------

  renderWeeklyHistory() {
    clearChildren(this.els.weeklyHistory);
    const entries = [...this.state.weeklyReflections].reverse(); // newest first

    for (const entry of entries) {
      const item = document.createElement('div');
      item.className = 'tool-journal-history-item';

      if (entry.createdAt) {
        const meta = document.createElement('span');
        meta.className = 'tool-journal-history-meta';
        meta.textContent = formatDate(entry.createdAt);
        item.appendChild(meta);
      }

      this.appendLabeledText(item, 'Pattern noticed', entry.pattern);
      this.appendLabeledText(item, 'Would change', entry.wouldChange);
      this.appendLabeledText(item, 'Next focus', entry.nextFocus);

      this.els.weeklyHistory.appendChild(item);
    }
  }

  handleAddWeeklyReflection() {
    const pattern = this.els.weeklyPatternInput.value.trim();
    const wouldChange = this.els.weeklyChangeInput.value.trim();
    const nextFocus = this.els.weeklyFocusInput.value.trim();
    if (!pattern && !wouldChange && !nextFocus) return;

    // addWeeklyReflection() no-ops (returns the same array reference) once
    // the entry cap is reached - without this check the form still resets
    // and "saved" still shows, silently discarding what the user just wrote.
    const countBefore = this.state.weeklyReflections.length;
    this.state = addWeeklyReflection(this.state, { pattern, wouldChange, nextFocus }, Date.now());
    if (this.state.weeklyReflections.length === countBefore) {
      this.showActionFeedback("You've reached the maximum number of weekly reflections - export or delete older ones to add more.");
      return;
    }

    this.markEngaged();
    this.els.weeklyForm.reset();
    this.renderWeeklyHistory();
    this.scheduleAutosave();
    this.showActionFeedback('Weekly reflection saved.');
    trackToolAction(TOOL_NAME, 'result', { tab: 'weekly-reflection' });
  }

  // --- Decision journal tab ---------------------------------------------

  renderDecisionHistory() {
    clearChildren(this.els.decisionHistory);
    const entries = [...this.state.decisionJournal].reverse(); // newest first

    for (const entry of entries) {
      const item = document.createElement('div');
      item.className = 'tool-journal-history-item';

      // h3, not h4 - the containing "Past decisions" section header is an h2
      // (content-section-title), so this is one level down, not two.
      const title = document.createElement('h3');
      title.textContent = entry.decision;
      item.appendChild(title);

      if (entry.createdAt) {
        const meta = document.createElement('span');
        meta.className = 'tool-journal-history-meta';
        meta.textContent = formatDate(entry.createdAt);
        item.appendChild(meta);
      }

      this.appendLabeledText(item, 'Facts known', entry.factsKnown);
      this.appendLabeledText(item, 'Assumptions', entry.assumptions);
      this.appendLabeledText(item, 'Emotional state', entry.emotionalState);
      this.appendLabeledText(item, 'Stakeholders', entry.stakeholders);
      this.appendLabeledText(item, 'Expected result', entry.expectedResult);

      if (entry.status === 'resolved') {
        this.appendLabeledText(item, 'Actual result', entry.actualResult);
        this.appendLabeledText(item, 'Repeat or change', entry.repeatOrChange);
      } else {
        item.appendChild(this.buildOutcomeForm(entry.id));
      }

      this.els.decisionHistory.appendChild(item);
    }
  }

  buildOutcomeForm(entryId) {
    const form = document.createElement('form');
    form.className = 'tool-journal-outcome-form';

    const actualLabel = document.createElement('label');
    actualLabel.textContent = 'Actual result (once known)';
    const actualInput = document.createElement('textarea');
    actualInput.className = 'form-control';
    actualInput.rows = 2;
    actualLabel.appendChild(actualInput);

    const repeatLabel = document.createElement('label');
    repeatLabel.textContent = 'Repeat or change next time?';
    const repeatInput = document.createElement('textarea');
    repeatInput.className = 'form-control';
    repeatInput.rows = 2;
    repeatLabel.appendChild(repeatInput);

    const saveBtn = document.createElement('button');
    saveBtn.type = 'submit';
    saveBtn.className = 'btn btn-outline-secondary btn-sm';
    saveBtn.textContent = 'Save outcome';

    form.append(actualLabel, repeatLabel, saveBtn);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const actualResult = actualInput.value.trim();
      const repeatOrChange = repeatInput.value.trim();
      if (!actualResult && !repeatOrChange) return;
      this.state = recordDecisionOutcome(this.state, entryId, { actualResult, repeatOrChange }, Date.now());
      this.renderDecisionHistory();
      this.scheduleAutosave();
      this.showActionFeedback('Outcome saved.');
      trackToolAction(TOOL_NAME, 'result', { tab: 'decision-outcome' });
    });

    return form;
  }

  handleAddDecisionEntry() {
    const decision = this.els.decisionInput.value.trim();
    if (!decision) return;

    // Same no-op-at-cap check as handleAddWeeklyReflection - see its comment.
    const countBefore = this.state.decisionJournal.length;
    this.state = addDecisionEntry(
      this.state,
      {
        decision,
        factsKnown: this.els.factsInput.value.trim(),
        assumptions: this.els.assumptionsInput.value.trim(),
        emotionalState: this.els.emotionalStateInput.value.trim(),
        stakeholders: this.els.stakeholdersInput.value.trim(),
        expectedResult: this.els.expectedInput.value.trim(),
      },
      Date.now()
    );
    if (this.state.decisionJournal.length === countBefore) {
      this.showActionFeedback("You've reached the maximum number of decision entries - export or delete older ones to add more.");
      return;
    }

    this.markEngaged();
    this.els.decisionForm.reset();
    this.renderDecisionHistory();
    this.scheduleAutosave();
    this.showActionFeedback('Decision logged.');
    trackToolAction(TOOL_NAME, 'result', { tab: 'decision-journal' });
  }

  // --- shared ---------------------------------------------

  appendLabeledText(container, label, text) {
    if (!text) return;
    const p = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = `${label}: `;
    p.appendChild(strong);
    p.appendChild(document.createTextNode(text));
    container.appendChild(p);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) {
      // Unlike the other tools here, the journal is designed for genuinely
      // large, long-running content (months of entries) - silently no-op'ing
      // like a fixed-size tool safely can would mean the user keeps
      // journaling into a store that quietly stopped persisting anything.
      this.showActionFeedback("Your journal is too large to keep saving automatically - export it now, then delete older entries.");
      return;
    }
    this.journalStore.save(toSave);
  }

  handleExport() {
    const markdown = buildMarkdownExport(this.state);
    download('leadership-practice-journal.md', 'text/markdown', markdown);
    this.showActionFeedback('Exported.');
    trackToolAction(TOOL_NAME, 'download');
  }

  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.els.startOverBtn.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.els.startOverBtn.textContent = 'Delete all my data';
      }, 4000);
      return;
    }

    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.els.startOverBtn.textContent = 'Delete all my data';
    this.journalStore.clear();
    this.state = createInitialState();
    this.renderAll();
    this.showActionFeedback('All journal data deleted from this browser.');
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!byId('journal-daily-form')) return;
  new LeadershipPracticeJournal();
});
